<template>
    <hox-modal resizable class="new-size-modal" @close="close">
        <template #header>New size</template>

        <div class="new-size-modal__body">
            <Select
                class="new-size-modal__predefined-values"
                :disabled="isLoadingMasterTemplates"
                @onChange="onDefaultSelected"
            >
                <OptionGroup label="Facebook">
                    <Option
                        v-for="(item, dcIndex) in platforms.facebook"
                        :key="getKey(item)"
                        :value="`facebook-${dcIndex}`"
                        :label="item.name"
                        :disabled="sizeAlreadyExists(item.width, item.height)"
                    >
                        <span>{{ item.name }}</span>
                        <span class="new-size-modal__predefined-values-right">
                            {{ formatPredefinedSize(item.width, item.height) }}
                        </span>
                    </Option>
                </OptionGroup>

                <OptionGroup label="Instagram">
                    <Option
                        v-for="(item, dcIndex) in platforms.instagram"
                        :key="getKey(item)"
                        :value="`instagram-${dcIndex}`"
                        :label="item.name"
                        :disabled="sizeAlreadyExists(item.width, item.height)"
                    >
                        <span>{{ item.name }}</span>
                        <span class="new-size-modal__predefined-values-right">
                            {{ formatPredefinedSize(item.width, item.height) }}
                        </span>
                    </Option>
                </OptionGroup>

                <OptionGroup label="TikTok">
                    <Option
                        v-for="(item, dcIndex) in platforms.tiktok"
                        :key="getKey(item)"
                        :value="`tiktok-${dcIndex}`"
                        :disabled="sizeAlreadyExists(item.width, item.height)"
                    >
                        <span>{{ item.name }}</span>
                        <span class="new-size-modal__predefined-values-right">
                            {{ formatPredefinedSize(item.width, item.height) }}
                        </span>
                    </Option>
                </OptionGroup>

                <OptionGroup label="YouTube">
                    <Option
                        v-for="(item, dcIndex) in platforms.youtube"
                        :key="getKey(item)"
                        :value="`youtube-${dcIndex}`"
                        :disabled="sizeAlreadyExists(item.width, item.height)"
                    >
                        <span>{{ item.name }}</span>
                        <span class="new-size-modal__predefined-values-right">
                            {{ formatPredefinedSize(item.width, item.height) }}
                        </span>
                    </Option>
                </OptionGroup>

                <OptionGroup label="Google Display">
                    <Option
                        v-for="(item, dcIndex) in platforms.doubleclick"
                        :key="getKey(item)"
                        :value="`doubleclick-${dcIndex}`"
                        :disabled="sizeAlreadyExists(item.width, item.height)"
                    >
                        <span>{{ item.name }}</span>
                        <span class="new-size-modal__predefined-values-right">
                            {{ formatPredefinedSize(item.width, item.height) }}
                        </span>
                    </Option>
                </OptionGroup>
            </Select>

            <div class="new-size-modal__form">
                <span class="new-size-modal__form-item">size</span>
                <span class="new-size-modal__form-input-wrapper new-size-modal__form-item">
                    <Input
                        type="number"
                        class="new-size-modal__form-input"
                        :model-value="width"
                        :disabled="isLoadingMasterTemplates"
                        @onChange="onInputChange('width', $event.target.value)"
                    >
                        <template #append>{{ unitLabel }}</template>
                    </Input>
                </span>
                <span class="new-size-modal__form-item">x</span>
                <span class="new-size-modal__form-input-wrapper new-size-modal__form-item">
                    <Input
                        type="number"
                        class="new-size-modal__form-input"
                        :model-value="height"
                        :disabled="isLoadingMasterTemplates"
                        @onChange="onInputChange('height', $event.target.value)"
                    >
                        <template #append>{{ unitLabel }}</template>
                    </Input>
                </span>
            </div>

            <div class="new-size-modal__form-errors">
                <ul>
                    <li v-for="error in validationErrors" :key="error">{{ error }}</li>
                </ul>
            </div>
            <div class="new-size-modal__banner" test-id="new-size__banner">
                <NewSizePreview
                    :master-template="masterTemplate"
                    :input-width="width"
                    :input-height="height"
                    @resized="onBannerResize"
                />
            </div>

            <div v-if="errorMessage && !isCreating" class="new-size-modal__error">
                <hox-alert type="danger" size="small" align-text="center">
                    <template #content>
                        <p>{{ errorMessage }}</p>
                    </template>
                </hox-alert>
            </div>
        </div>

        <template #footer>
            <div>
                <Button class="new-size-modal__footer-button" @click="close">Close</Button>
                <Button
                    class="new-size-modal__footer-button"
                    type="primary"
                    :loading="isCreating || isLoadingMasterTemplates"
                    :disabled="isLoadingMasterTemplates || isCreating || !!validationErrors.length"
                    @click="create"
                >
                    {{ isCreating ? "Creating..." : "Create" }}
                </Button>
            </div>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { MasterTemplate } from "hoxton-types";

import clientMasterTemplatesQuery from "@/apollo/queries/v2/ClientMasterTemplates.gql";

import HoxModal from "@/components/Modal/Modal/Modal.vue";
import NewSizePreview from "@/components/Campaign/NewSizePreview.vue";
import { resizeMasterTemplate } from "@/services/masterTemplates/resizeMasterTemplate";
import { convertPixelsToIndesignDimension, getIndesignUnitLabel } from "hoxton-shared/utils/indesignMeasurementUnit";

const PLATFORMS = {
    facebook: [
        { width: 1080, height: 1080, name: "Feed (1:1)" },
        { width: 1080, height: 1285, name: "Portrait Feed (1.19:1)" },
        { width: 1080, height: 1920, name: "Story (9:16)" }
    ],

    instagram: [
        { width: 1080, height: 1080, name: "Feed (1:1)" },
        { width: 1080, height: 1920, name: "Story (9:16)" }
    ],

    tiktok: [
        { width: 1080, height: 1080, name: "In-Feed Square (1:1)" },
        { width: 1080, height: 1920, name: "In-Feed Story (9:16)" },
        { width: 1920, height: 1080, name: "In-Feed Story (16:9)" }
    ],

    youtube: [
        { width: 169, height: 1080, name: "Story (9:16)" },
        { width: 1920, height: 1080, name: "In-stream Video (16:9)" }
    ],

    doubleclick: [
        { width: 200, height: 200, name: "Small square" },
        { width: 240, height: 400, name: "Vertical rectangle" },
        { width: 250, height: 250, name: "Square" },
        { width: 250, height: 360, name: "Triple widescreen" },
        { width: 300, height: 250, name: "Inline rectangle" },
        { width: 336, height: 280, name: "Large rectangle" },
        { width: 580, height: 400, name: "Netboard" },

        { width: 120, height: 600, name: "Skyscraper" },
        { width: 160, height: 600, name: "Wide skyscraper" },
        { width: 300, height: 600, name: "Half-page ad" },
        { width: 300, height: 1050, name: "Portrait" },

        { width: 468, height: 60, name: "Banner" },
        { width: 728, height: 90, name: "Leaderboard" },
        { width: 930, height: 180, name: "Top banner" },
        { width: 970, height: 90, name: "Large leaderboard" },
        { width: 970, height: 250, name: "Billboard" },
        { width: 980, height: 120, name: "Panorama" },

        { width: 300, height: 50, name: "Mobile banner" },
        { width: 320, height: 50, name: "Mobile banner" },
        { width: 320, height: 100, name: "Large mobile banner" }
    ]
} as const;

export default defineComponent({
    components: {
        NewSizePreview,
        HoxModal
    },
    props: {
        masterTemplate: {
            type: Object as PropType<MasterTemplate>,
            required: true
        }
    },

    emits: ["close"],

    data() {
        return {
            isCreating: false,
            width: 100,
            height: 100,
            validationErrors: [] as string[],
            platforms: PLATFORMS,
            errorMessage: "",
            isLoadingMasterTemplates: false,
            familyTemplates: [] as MasterTemplate[]
        };
    },

    computed: {
        familyMembers() {
            return this.familyTemplates.filter(({ _id, parentId }) =>
                [_id, parentId].includes(this.masterTemplate._id)
            );
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        canManageOverwrites() {
            return this.$auth.userCan(this.$auth.Actions.CanManageOverwrites, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        unitLabel() {
            if (this.masterTemplate.adType === "INDESIGN") {
                const unit = (this.masterTemplate as { measurementUnit?: string }).measurementUnit;
                return getIndesignUnitLabel(unit) || "pt";
            }
            return "px";
        }
    },

    mounted() {
        this.height = this.masterTemplate.height;
        this.width = this.masterTemplate.width;
    },

    methods: {
        onBannerResize({ width, height }: { width: number; height: number }) {
            if (this.masterTemplate.adType === "INDESIGN") {
                return;
            }

            this.width = Math.round(width);
            this.height = Math.round(height);
        },

        close() {
            this.$emit("close");
        },

        async create() {
            this.isCreating = true;

            try {
                await resizeMasterTemplate({
                    width: this.width,
                    height: this.height,
                    clientId: this.$route.params.clientId as string,
                    masterTemplate: this.masterTemplate,
                    masterTemplateId: this.masterTemplate._id,
                    campaignId: this.$route.params.campaignId as string,
                    sandboxMode: this.$store.state.editor.isSandboxMode
                });
                this.$snackbar.success("Master template has been created.");
                this.close();
            } catch (e) {
                this.errorMessage = e as string;
            }

            this.isCreating = false;
        },

        getKey(item: { width: number; height: number; name: string }) {
            return `${item.width}${item.height}${item.name}`;
        },

        onDefaultSelected(item: string) {
            const [platform, index] = item.split("-");
            const size = this.platforms[platform as keyof typeof PLATFORMS][Number(index)];

            if (!size) {
                return;
            }

            this.onInputChange("width", this.getPresetInputDimension(size.width).toString());
            this.onInputChange("height", this.getPresetInputDimension(size.height).toString());
        },

        getPresetInputDimension(value: number) {
            if (this.masterTemplate.adType === "INDESIGN") {
                const unit = (this.masterTemplate as { measurementUnit?: string }).measurementUnit;
                return Math.round(convertPixelsToIndesignDimension(value, unit) * 1000) / 1000;
            }
            return value;
        },

        onInputChange(prop: "width" | "height", inputVal: string) {
            if (inputVal === "") {
                return;
            }

            const val = parseFloat(inputVal);

            if (val === this[prop]) {
                return;
            }

            this[prop] = val;

            if (this.width > 4096) {
                this.width = 4096;
            }

            this.validate();
        },

        validate() {
            this.validationErrors = [
                ...this.validateSizeValue("width", this.width),
                ...this.validateSizeValue("height", this.height),
                ...this.validateFamilySizePresent(this.width, this.height)
            ];
        },

        validateSizeValue(prop: "width" | "height", value: number) {
            const errors = [];

            if (Number.isNaN(value)) {
                errors.push(`${prop} value is not a valid number`);

                return errors;
            }

            if (value > 4096) {
                errors.push(`${prop} can't be larger than 4096${this.unitLabel}`);
            }

            if (value < 0) {
                errors.push(`${prop} can't be a negative number`);
            }

            return errors;
        },

        sizeAlreadyExists(width: number, height: number) {
            if (this.familyMembers.some(member => member.width === width && member.height === height)) {
                return true;
            }

            return false;
        },

        validateFamilySizePresent(width: number, height: number) {
            if (this.sizeAlreadyExists(width, height)) {
                return [`Template family already contains a template with size ${this.formatSize(width, height)}`];
            }
            return [];
        },

        formatSize(width: number, height: number) {
            if (this.masterTemplate.adType === "INDESIGN") {
                return `${width}${this.unitLabel} x ${height}${this.unitLabel}`;
            }
            return `${width} x ${height}`;
        },

        formatPredefinedSize(width: number, height: number) {
            return `${width}px x ${height}px`;
        }
    },
    apollo: {
        masterTemplatesByClient: {
            query: clientMasterTemplatesQuery,
            loadingKey: "isLoadingMasterTemplates",
            fetchPolicy: "network-only",
            variables() {
                return {
                    clientId: this.clientId
                };
            },

            skip() {
                return !this.canManageOverwrites;
            },

            async result({ data }) {
                this.familyTemplates = data.masterTemplatesByClient;
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.new-size-modal {
    &__body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    &__predefined-values {
        width: 100%;
        margin-bottom: $spacing;

        &-right {
            float: right;
            color: #cccccc;
        }
    }

    &__form {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        margin-bottom: $spacing-smaller;

        &-item {
            flex: 0 0;
            margin: 0 $spacing-small;
        }

        &-input-wrapper {
            display: block;
            white-space: nowrap;
            margin: 0 $spacing-smaller;
        }

        &-input {
            width: 85px;

            /* Chrome, Safari, Edge, Opera */
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                appearance: none;
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            input[type="number"] {
                appearance: textfield;
                -moz-appearance: textfield;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
                border-right-width: 0.5px;
            }

            .ivu-input-group-append {
                border-radius: 8px;
                border-color: var(--wpp-grey-color-600);
            }
        }
    }

    &__form-errors {
        margin-bottom: $spacing;
        color: $red;
    }

    &__footer-button {
        margin: 0 0 0 $spacing-small;
    }
    &__error {
        width: 100%;
        margin-top: $spacing-large;
    }
}
</style>
